import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bike, CheckCircle2, ClipboardList, Inbox } from "lucide-react";
import { BlurFade, NumberTicker } from "@tarawa/ui";
import { api, ApiError } from "@/lib/api";
import { errorMessage, type OrderCard as Order, type Summary } from "@/lib/driver";
import { useToast } from "@/lib/toast";
import { OrderCard } from "@/components/OrderCard";
import { Button, Empty, ErrorBox, Loading } from "@/components/ui";

const REFRESH_MS = 20_000;

function Stat({ label, value, icon: Icon }: { label: string; value: number | undefined; icon: typeof Inbox }) {
  return (
    <div className="rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)]">
      <div className="flex items-center justify-between">
        <span className="flex size-9 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
          <Icon className="size-5" />
        </span>
        <span className="text-2xl font-extrabold tabular-nums" data-testid={`stat-${label}`}>
          {value === undefined ? "–" : value === 0 ? "0" : <NumberTicker value={value} />}
        </span>
      </div>
      <p className="mt-2 text-xs font-semibold text-muted">{label}</p>
    </div>
  );
}

export default function HomePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();

  const summary = useQuery({ queryKey: ["summary"], queryFn: () => api<Summary>("/summary"), refetchInterval: REFRESH_MS });
  const available = useQuery({
    queryKey: ["available"],
    queryFn: () => api<{ orders: Order[] }>("/orders/available").then((r) => r.orders),
    refetchInterval: REFRESH_MS,
  });

  const accept = useMutation({
    mutationFn: (id: string) => api<{ order: { id: string } }>(`/orders/${id}/accept`, { method: "POST" }),
    onSuccess: ({ order }) => {
      toast("Commande acceptée : elle est dans « Mes commandes ».");
      void qc.invalidateQueries();
      navigate(`/commandes/${order.id}`);
    },
    onError: (err) => {
      toast(errorMessage(err), "error");
      // Another driver was faster (or the order changed): refresh the lists.
      if (err instanceof ApiError && err.status === 409) {
        void qc.invalidateQueries({ queryKey: ["available"] });
        void qc.invalidateQueries({ queryKey: ["summary"] });
      }
    },
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Nouvelles commandes" value={summary.data?.available} icon={Inbox} />
        <Stat label="Acceptées" value={summary.data?.accepted} icon={ClipboardList} />
        <Stat label="En cours de livraison" value={summary.data?.inProgress} icon={Bike} />
        <Stat label="Livrées aujourd'hui" value={summary.data?.deliveredToday} icon={CheckCircle2} />
      </div>
      {summary.error && <ErrorBox message={errorMessage(summary.error)} onRetry={() => summary.refetch()} />}

      <section className="space-y-3">
        <div className="flex items-end justify-between">
          <h1 className="text-xl font-extrabold tracking-tight">Nouvelles commandes disponibles</h1>
          <span className="text-xs text-subtle">Actualisation auto.</span>
        </div>
        {available.isLoading ? (
          <Loading />
        ) : available.error ? (
          <ErrorBox message={errorMessage(available.error)} onRetry={() => available.refetch()} />
        ) : available.data?.length === 0 ? (
          <Empty>Aucune commande disponible pour le moment.</Empty>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {available.data?.map((o, i) => (
              <BlurFade key={o.id} delay={Math.min(i, 6) * 0.03}>
                <OrderCard
                  order={o}
                  action={
                    <Button
                      size="lg"
                      className="w-full"
                      onClick={() => accept.mutate(o.id)}
                      loading={accept.isPending && accept.variables === o.id}
                      disabled={accept.isPending}
                    >
                      Accepter la commande
                    </Button>
                  }
                />
              </BlurFade>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
